import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Detects that a newer build has been deployed while this tab stayed open, and
 * nudges — at most once per interval — to reload onto it.
 *
 * Why: two days after v4.81.60 shipped, 248 unique users were still erroring on
 * v4.81.57, roughly two thirds of everyone hitting a bug, because a tab that is
 * never reloaded keeps running the JavaScript it booted with.
 *
 * Opening a page fresh already gets the latest build, so this deliberately does
 * nothing clever — no forced reload, no navigation interception. It speaks only
 * to people who never reload, and stays quiet otherwise: we deploy most days,
 * and a daily prompt would just get dismissed.
 *
 * Gated on the `feStaleBuildNudge` flag, off in production until verified.
 *
 * Detection compares the entry script this tab booted with against the one the
 * server is serving now. `index.html` is a safe probe because CloudFront is
 * fully invalidated on every deploy, and it needs no new build artifact.
 *
 * Every failure path here is deliberately silent — unparseable HTML, an offline
 * tab, a cached response all mean "cannot tell", never "assume stale". The worst
 * realistic outcome is one unnecessary refresh prompt, which is why this is a
 * better fit than a service worker whose worst case is pinning users to a build
 * they cannot escape.
 */

// Paced to the nudge, not to freshness: we surface this at most once every two
// days, so checking more than hourly cannot change what any user ever sees.
const POLL_INTERVAL_MS = 60 * 60 * 1000;
// Proportional so the spread scales with the interval, and so a deploy doesn't
// have every open tab probing in lockstep.
const JITTER_RATIO = 0.2;

// Floor between checks whatever the trigger. Without it, alt-tabbing fires a
// request per tab switch — the interval is not the cost, the focus events are.
const MIN_CHECK_GAP_MS = 15 * 60 * 1000;

// 3h while we validate this on staging. Raise to ~48h before enabling in
// production — we deploy most days, and a prompt per deploy is just noise.
export const NUDGE_INTERVAL_HOURS = 3;
export const NUDGE_INTERVAL_MS = NUDGE_INTERVAL_HOURS * 60 * 60 * 1000;
const NUDGE_STORAGE_KEY = 'kula:stale-build-nudged-at';

/**
 * Parsed rather than regexed: a regex over markup breaks the moment the build
 * emits the tag differently, and the failure would be silent.
 */
const entryScriptFrom = (html: string): string | null => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return firstOwnModuleScript(doc);
  } catch {
    return null;
  }
};

/**
 * Same-origin only. Pylon, Intercom, Clarity and Beamer all inject scripts at
 * runtime; comparing one of those against our entry would differ forever and
 * nudge on every interval regardless of staleness.
 */
const toPath = (src: string | null): string | null => {
  if (!src) return null;
  try {
    const url = new URL(src, window.location.origin);
    return url.origin === window.location.origin ? url.pathname : null;
  } catch {
    return null;
  }
};

const firstOwnModuleScript = (root: ParentNode): string | null => {
  const els = root.querySelectorAll<HTMLScriptElement>(
    'script[type="module"][src]'
  );
  for (const el of Array.from(els)) {
    const path = toPath(el.getAttribute('src'));
    if (path) return path;
  }
  return null;
};

const currentEntryScript = (): string | null => firstOwnModuleScript(document);

const readLastNudgedAt = (): number => {
  try {
    const at = Number(window.localStorage.getItem(NUDGE_STORAGE_KEY)) || 0;
    // A backwards clock correction would otherwise leave this permanently in
    // the future and stop checking for good.
    return at > Date.now() ? 0 : at;
  } catch {
    return 0;
  }
};

const writeLastNudgedAt = (at: number): void => {
  try {
    window.localStorage.setItem(NUDGE_STORAGE_KEY, String(at));
  } catch {
    // Blocked storage just means the throttle does not survive a reload.
  }
};

export interface StaleBuildCheck {
  /** A newer build is live and the throttle allows saying so. */
  isStale: boolean;
  /** Reload onto the new build. */
  reload: () => void;
  /** Hide until the next interval elapses. */
  dismiss: () => void;
}

export interface StaleBuildCheckOptions {
  enabled?: boolean;
  intervalMs?: number;
  /** Minimum gap between nudges. Defaults to NUDGE_INTERVAL_HOURS (3). */
  nudgeIntervalMs?: number;
}

export const useStaleBuildCheck = ({
  enabled = !import.meta.env.DEV,
  intervalMs = POLL_INTERVAL_MS,
  nudgeIntervalMs = NUDGE_INTERVAL_MS,
}: StaleBuildCheckOptions = {}): StaleBuildCheck => {
  const [isStale, setIsStale] = useState(false);
  const bootedWith = useRef<string | null>(null);
  if (bootedWith.current === null) bootedWith.current = currentEntryScript();
  const lastCheckedAt = useRef(0);

  const check = useCallback(async () => {
    if (!enabled || document.visibilityState !== 'visible') return;
    const booted = bootedWith.current;
    if (!booted) return;
    if (Date.now() - lastCheckedAt.current < MIN_CHECK_GAP_MS) return;
    // Cheap guard before the request: after a nudge there is nothing to look
    // for until the interval elapses, so two days pass with zero network use.
    if (Date.now() - readLastNudgedAt() < nudgeIntervalMs) return;
    lastCheckedAt.current = Date.now();

    try {
      const res = await fetch(`${window.location.origin}/index.html`, {
        cache: 'no-store',
        credentials: 'omit',
      });
      if (!res.ok) return;
      const served = entryScriptFrom(await res.text());
      // Cannot tell, or unchanged — either way, say nothing.
      if (!served || served === booted) return;
      writeLastNudgedAt(Date.now());
      setIsStale(true);
    } catch {
      // Offline or blocked: staying quiet is correct, this is advisory only.
    }
  }, [enabled, nudgeIntervalMs]);

  useEffect(() => {
    if (!enabled) return undefined;

    const timer = window.setInterval(
      check,
      intervalMs * (1 + Math.random() * JITTER_RATIO)
    );
    // Returning to a tab left open overnight is the best moment to look.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check, enabled, intervalMs]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  // The interval was recorded when the nudge appeared, so dismissing only has
  // to hide it — the next one is already due `nudgeIntervalMs` from now.
  const dismiss = useCallback(() => setIsStale(false), []);

  return { isStale, reload, dismiss };
};
